import React, { useState, useEffect } from 'react';
import {
    Box,
    SimpleGrid,
    Text,
    Badge,
    VStack,
    Container,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    Input,
    useDisclosure,
    useToast,
    HStack,
    Heading,
    Spacer,
    IconButton,
    Center
} from '@chakra-ui/react';
import { useAuction } from '../context/AuctionContext';
import { motion } from 'framer-motion';
import CountdownTimer from './CountdownTimer';
import { getDayStartDate, getDayEndDate, AUCTION_START_DATE } from '../utils/timerUtils';
import { FaTwitter } from 'react-icons/fa';
import { 
    TOTAL_DAYS, 
    BONUS_DAYS, 
    DAILY_ALLOCATION,
    getBonusMultiplier,
    DEX_LISTING_DAY,
    AUCTION_DAYS,
    TOTAL_SIMULATED_JINU,
    getSimulatedData,
    SIMULATED_DATA
} from '../utils/constants';
import DexListingBox from './DexListingBox';
import SolanaLogo from './SolanaLogo';
import { calculateDailySupply, getDayStatus, isValidContributionDay, isBonus } from '../utils/auctionCore';
import { calculateUserAllocation, simulateDailyAllocation } from '../utils/allocationManager';
import { validateContribution } from '../utils/contributionManager';

const MotionBox = motion(Box);

const DailyBoxes = ({ 
    currentDay = 1, 
    userTotalJinu = 0, 
    compact = false, 
    dailyAllocations = [], 
    handleDayClick: onDayClick, 
    projectedContributions = {},
    excludeDays = [] // Array of days to exclude from rendering
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [contributionAmount, setContributionAmount] = useState('');
    const [selectedDayState, setSelectedDayState] = useState(null);
    const auction = useAuction(); // Get auction context at the component level
    const toast = useToast();

    const TOTAL_JINU = 37500000;
    const FLUCTUATION = 0.35; // 35% fluctuation

    // Calculate daily values based on day number
    const calculateDailyValues = (day) => {
        if (day > TOTAL_DAYS) return { totalUsers: 0, totalAmount: 0, allocationPercentage: 0, fluctuationPercentage: 0 };
        
        // Get simulated data directly from the constants
        const simData = getSimulatedData(day);
        
        // Get user contributions for this day from the auction context we already have
        const userContribs = auction?.auctionState?.userContributions?.[day] || [];
        const userTotal = userContribs.reduce((sum, contrib) => sum + contrib.amount, 0);
        
        // Get projected contributions if any
        const projectedAmount = auction?.projectedContributions?.[day] || 0;
        
        // Add user contributions to simulated data
        const totalAmount = (simData.sol || 0) + userTotal;
        const totalUsers = (simData.users || 0) + (userTotal > 0 ? 1 : 0);
        
        const allocation = simData.jinu || 0;
        const totalAllocation = TOTAL_SIMULATED_JINU;
        const allocationPercentage = ((allocation / totalAllocation) * 100).toFixed(1);
        
        // Calculate fluctuation percentage from base amount (750,000)
        const baseAmount = 750000; // Base JINU amount
        const fluctuationPercentage = (((allocation - baseAmount) / baseAmount) * 100).toFixed(1);
        
        return {
            totalUsers: totalUsers,
            totalAmount: totalAmount,
            projectedAmount: projectedAmount,
            userContribution: userTotal,
            allocationPercentage,
            dayAllocation: allocation,
            fluctuationPercentage
        };
    };

    const [activeDay, setActiveDay] = useState(currentDay);
    const currentDayRef = React.useRef(null);

    useEffect(() => {
        // Scroll to current day when component mounts
        if (currentDayRef.current) {
            setTimeout(() => {
                if (currentDayRef.current) {
                    currentDayRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'center'
                    });
                }
            }, 100); // Small delay to ensure component is fully rendered
        }
    }, []); // Only run once on mount

    const getDayDate = (day) => {
        // Create a new date object from the string constant
        const startDate = new Date(AUCTION_START_DATE);
        // Create a new date for the calculation to avoid modifying the original
        const date = new Date(startDate);
        // Add the day offset (day - 1 since day 1 is the start date)
        date.setDate(date.getDate() + (day - 1));
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getTimerDate = (day) => {
        // Calculate the base days to add from day 1
        const daysFromStart = day - 1;
        
        // Create target date starting from auction start
        const startDate = new Date(AUCTION_START_DATE);
        const targetDate = new Date(startDate);
        
        // Add the days from start plus one more day (for the 24h countdown)
        targetDate.setDate(targetDate.getDate() + daysFromStart + 1);
        targetDate.setHours(0, 0, 0, 0);
        
        return targetDate;
    };

    const formatTimeRemaining = (ms) => {
        if (ms < 0) return '00:00:00';
        
        const seconds = Math.floor((ms / 1000) % 60);
        const minutes = Math.floor((ms / (1000 * 60)) % 60);
        const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);
        const days = Math.floor(ms / (1000 * 60 * 60 * 24));

        return days > 0 
            ? `${days}d ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            : `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const calculateUserAllocation = (dayAllocation, userContribution, totalSol) => {
        if (!userContribution || !totalSol || !dayAllocation) {
            return {
                sharePercentage: 0,
                allocation: 0
            };
        }
        
        const sharePercentage = (userContribution / totalSol) * 100;
        const share = sharePercentage / 100;
        const baseAllocation = dayAllocation * share;
        
        // Apply bonuses
        const bonuses = auction.calculateContributionBonus(userContribution);
        const finalAllocation = baseAllocation * (1 + (bonuses.totalBonus / 100));
        
        return {
            sharePercentage: parseFloat(sharePercentage.toFixed(2)),
            allocation: parseFloat(finalAllocation.toFixed(2))
        };
    };

    const getDayBoxContent = (day) => {
        if (day === DEX_LISTING_DAY) {
            return {
                type: 'dex',
                title: 'DEX Listing',
                description: 'JINU Token DEX Listing Day'
            };
        }

        const allocation = calculateDailySupply(day);
        const dayStats = auction.getDayStats(day);
        
        if (!allocation || !dayStats) {
            return {
                type: 'empty',
                title: 'No Data',
                description: 'No allocation data available'
            };
        }

        const { amount, bonus } = allocation;
        const { userContribution = 0, totalSol = 0 } = dayStats;

        return {
            type: 'auction',
            allocation: amount,
            bonus,
            userContribution,
            totalSol
        };
    };

    const formatNumber = (num, decimals = 0) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(num || 0);
    };

    const getBonusText = (day) => {
        if (day > 50) return '';
        const allocation = calculateDailySupply(day);
        return allocation ? allocation.bonus : '+0%';
    };

    const getBoxColor = (day) => {
        // Special case for DEX listing day
        if (day === DEX_LISTING_DAY) return 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)'; // More solid electric blue for DEX listing

        // Phase colors (non-active, non-claimable days)
        if (day <= 15) return 'rgba(66, 153, 225, 0.2)'; // Phase 1 - transparent blue
        if (day <= 30) return 'rgba(159, 122, 234, 0.2)'; // Phase 2 - transparent purple
        if (day <= 40) return 'rgba(49, 151, 149, 0.2)'; // Phase 3 - transparent teal
        if (day <= 50) return 'rgba(98, 126, 234, 0.2)'; // Phase 4 - transparent indigo
        return 'rgba(0, 191, 255, 0.4)';
    };

    const getBorderColor = (day) => {
        // Special case for DEX listing day
        if (day === DEX_LISTING_DAY) return 'blue.400'; // More solid electric blue border

        // Darker border colors
        if (day <= 15) return 'rgba(66, 153, 225, 0.4)';
        if (day <= 30) return 'rgba(159, 122, 234, 0.4)';
        if (day <= 40) return 'rgba(49, 151, 149, 0.4)';
        if (day <= 50) return 'rgba(98, 126, 234, 0.4)';
        return 'rgba(0, 191, 255, 0.6)';
    };

    const getPhaseText = (day) => {
        // Phase text with bonus percentage
        if (day <= 15) return 'Phase 1: +5%';
        if (day <= 30) return 'Phase 2: +3%';
        if (day <= 40) return 'Phase 3: +1%';
        if (day <= 50) return 'Phase 4: +0.5%';
        return 'DEX Listing';
    };

    const [timeRemaining, setTimeRemaining] = useState('');

    useEffect(() => {
        const calculateTimeRemaining = () => {
            const now = new Date();
            
            // Calculate 24h box timer (time until next UTC midnight)
            const nextMidnight = new Date(now);
            nextMidnight.setUTCHours(24, 0, 0, 0);
            const dayTimeDiff = nextMidnight - now;

            if (dayTimeDiff <= 0) {
                setTimeRemaining('00:00:00');
                return;
            }

            const hours = Math.floor(dayTimeDiff / (1000 * 60 * 60));
            const minutes = Math.floor((dayTimeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((dayTimeDiff % (1000 * 60)) / 1000);

            setTimeRemaining(
                `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
            );
        };

        calculateTimeRemaining();
        const timer = setInterval(calculateTimeRemaining, 1000);
        return () => clearInterval(timer);
    }, []);

    const generateDailyAllocations = () => {
        const baseAllocation = TOTAL_JINU / 50; // Use 50 instead of TOTAL_DAYS
        
        // Generate random allocations with fluctuation for auction days only
        let allocations = Array.from({ length: 50 }, (_, i) => {
            const fluctuationAmount = baseAllocation * FLUCTUATION;
            const randomFluctuation = (Math.random() * 2 - 1) * fluctuationAmount;
            return Math.max(baseAllocation + randomFluctuation, 0);
        });

        // Add DEX listing day with no allocation
        allocations.push(0);

        // Adjust auction days to ensure total equals TOTAL_JINU
        const auctionAllocations = allocations.slice(0, 50);
        const currentTotal = auctionAllocations.reduce((sum, val) => sum + val, 0);
        const adjustmentFactor = TOTAL_JINU / currentTotal;
        
        // Update only auction days (0-49)
        allocations = [
            ...auctionAllocations.map(val => Math.round(val * adjustmentFactor)),
            0 // Keep DEX listing day allocation as 0
        ];

        // Final adjustment for any rounding errors
        const finalTotal = allocations.slice(0, 50).reduce((sum, val) => sum + val, 0);
        if (finalTotal !== TOTAL_JINU) {
            const difference = TOTAL_JINU - finalTotal;
            allocations[0] += difference; // Add any remaining to first day
        }

        return allocations;
    };

    const generatedDailyAllocations = generateDailyAllocations();

    const handleTwitterShare = (day) => {
        const tweetText = `Just participated in Day ${day} of the JINU Token Auction! 🚀 Join now and get ${getBonusText(day)} bonus! #JINU #Solana`;
        const url = 'https://your-auction-url.com'; // Replace with actual URL
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}`, '_blank');
    };

    const getRank = (jinuAmount) => {
        if (jinuAmount >= 100000) return { rank: 'Whale 🐋', color: 'blue.300' };
        if (jinuAmount >= 50000) return { rank: 'Shark 🦈', color: 'purple.300' };
        if (jinuAmount >= 10000) return { rank: 'Crab 🦀', color: 'orange.300' };
        return { rank: 'Shrimp 🦐', color: 'pink.300' };
    };

    const StatusDot = ({ isActive }) => (
        <Box
            as="span"
            display="inline-block"
            w="8px"
            h="8px"
            borderRadius="full"
            bg={isActive ? "red.500" : "transparent"}
            mr={2}
            animation={isActive ? "pulse 2s infinite" : "none"}
            sx={{
                "@keyframes pulse": {
                    "0%": {
                        transform: "scale(0.95)",
                        boxShadow: "0 0 0 0 rgba(255, 0, 0, 0.7)",
                    },
                    "70%": {
                        transform: "scale(1)",
                        boxShadow: "0 0 0 10px rgba(255, 0, 0, 0)",
                    },
                    "100%": {
                        transform: "scale(0.95)",
                        boxShadow: "0 0 0 0 rgba(255, 0, 0, 0)",
                    },
                },
            }}
        />
    );

    const getStatusBadge = (status) => {
        switch(status) {
            case 'today':
                return (
                    <HStack spacing={1}>
                        <StatusDot isActive={true} />
                        <Text color="green.400" fontWeight="bold" fontSize="sm">
                            LIVE
                        </Text>
                    </HStack>
                );
            case 'past':
                return (
                    <Text color="red.400" fontWeight="bold" fontSize="sm">
                        CLOSED
                    </Text>
                );
            case 'future':
                return (
                    <Text color="orange.400" fontWeight="bold" fontSize="sm">
                        UPCOMING
                    </Text>
                );
            default:
                return null;
        }
    };

    const handleConnectWallet = async () => {
        try {
            if (auction.wallet.connect) {
                await auction.wallet.connect();
                // Skip wallet connected toast to prevent duplicate messages
                console.log("Wallet connected in DailyBoxes");
            }
        } catch (error) {
            toast({
                title: "Failed to connect wallet",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleDaySelection = async (day) => {
        // Check wallet connection first
        if (!auction.wallet.connected) {
            try {
                await auction.wallet.connect();
            } catch (error) {
                toast({
                    title: "Please connect your wallet",
                    description: "You need to connect your wallet before making a reservation",
                    status: "warning",
                    duration: 3000,
                    isClosable: true
                });
                return;
            }
        }
        
        setSelectedDayState(day);
        onOpen();
    };

    const validateContribution = () => {
        if (!auction.wallet.connected) {
            toast({
                title: "Please connect your wallet",
                status: "warning",
                duration: 3000,
                isClosable: true
            });
            return false;
        }

        if (!contributionAmount || parseFloat(contributionAmount) <= 0) {
            toast({
                title: "Invalid amount",
                description: "Please enter a valid SOL amount",
                status: "error",
                duration: 3000,
                isClosable: true
            });
            return false;
        }

        return true;
    };

    const handleSuccess = (message) => {
        toast({
            title: "Contribution successful",
            description: message,
            status: "success",
            duration: 3000,
            isClosable: true
        });
        onClose();
        setContributionAmount('');
    };

    const handleError = () => {
        toast({
            title: "Contribution failed",
            description: "Failed to process your contribution",
            status: "error",
            duration: 3000,
            isClosable: true
        });
    };

    const handleModalContribute = async () => {
        if (!validateContribution()) return;

        const success = await auction.handleContribute(parseFloat(contributionAmount), selectedDayState);
        if (success) {
            handleSuccess(`Successfully contributed ${contributionAmount} SOL to Day ${selectedDayState}`);
        } else {
            handleError();
        }
    };

    const getDayContribution = (day) => {
        if (day > 50) {
            return {
                userContribution: 0,
                totalContribution: 0,
                sharePercentage: 0,
                allocation: 0
            };
        }

        const allocation = calculateDailySupply(day);
        const dayStats = auction.getDayStats(day);
        
        if (!allocation || !dayStats) {
            return {
                userContribution: 0,
                totalContribution: 0,
                sharePercentage: 0,
                allocation: 0
            };
        }

        return {
            userContribution: dayStats.totalUserContribution || 0,
            totalContribution: dayStats.totalContribution || 0,
            sharePercentage: dayStats.sharePercentage || 0,
            allocation: allocation
        };
    };

    const BASE_DAILY_ALLOCATION = 750000; // 37,500,000 / 50 days
    const FLUCTUATION_RANGE = 0.35; // 35% fluctuation

    const renderDayBox = (day) => {
        if (excludeDays.includes(day)) return null;

        const isCurrentDay = day === currentDay;
        const isSelected = day === selectedDayState;
        const isPast = day < currentDay;
        const isFuture = day > currentDay;
        
        return (
            <MotionBox
                ref={isCurrentDay ? currentDayRef : null}
                key={day}
                position="relative"
                bg={getBoxColor(day)}
                borderWidth="1px"
                borderColor={getBorderColor(day)}
                borderRadius="xl"
                p={4}
                transition="all 0.2s"
                _hover={{
                    transform: 'translateY(-2px)',
                    boxShadow: 'xl'
                }}
                height="0"
                paddingBottom="100%"
                overflow="hidden"
            >
                <VStack
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    p={4}
                    spacing={2}
                    align="stretch"
                >
                    {/* Day & Timer */}
                    <HStack justify="space-between" align="center">
                        <Text color="white" fontSize="md" fontWeight="bold">
                            Day {day}
                        </Text>
                        <CountdownTimer 
                            targetDate={getTimerDate(day)} 
                            mode="compact"
                            fontSize="xs"
                            color="white"
                        />
                    </HStack>

                    {/* Date & Status */}
                    <HStack justify="space-between" align="center">
                        <Text fontSize="xs" color="gray.400">
                            {getDayDate(day)}
                        </Text>
                        {getStatusBadge(getDayStatus(day))}
                    </HStack>

                    {/* Phase & Allocation % & Bonus */}
                    <HStack justify="space-between" align="center">
                        <Text fontSize="xs" color="gray.300">
                            {getPhaseText(day)}
                        </Text>
                        <HStack spacing={2}>
                            <Badge 
                                colorScheme={parseFloat(calculateDailyValues(day).fluctuationPercentage) >= 0 ? "green" : "red"}
                                variant="solid"
                            >
                                {calculateDailyValues(day).fluctuationPercentage > 0 ? '+' : ''}{calculateDailyValues(day).fluctuationPercentage}%
                            </Badge>
                        </HStack>
                    </HStack>

                    {/* Users & Total Amount */}
                    <HStack justify="space-between" align="center">
                        <Text fontSize="xs" color="gray.400">
                            Users: {calculateDailyValues(day).totalUsers}
                        </Text>
                        <Text fontSize="xs" color="gray.400">
                            {formatNumber(calculateDailyValues(day).totalAmount)} SOL
                        </Text>
                    </HStack>

                    {/* Add spacing */}
                    <Box height={2} />

                    {/* Total Allocation */}
                    <Center>
                        <HStack spacing={2}>
                            <Text fontSize="2xl" fontWeight="bold" color="white">
                                {formatNumber(calculateDailyValues(day).dayAllocation)}
                            </Text>
                            <Text fontSize="lg" color="gray.400">
                                JINU
                            </Text>
                        </HStack>
                    </Center>

                    {/* Add spacing */}
                    <Box height={2} />

                    {/* User Share & Buttons */}
                    <VStack spacing={2} mt="auto" width="100%">
                        {/* Always show user share */}
                        <Box 
                            width="100%" 
                            p={2} 
                            bg="whiteAlpha.100" 
                            borderRadius="md"
                        >
                            <VStack spacing={1}>
                                <Text fontSize="xs" color="gray.400">
                                    Your Contribution: 
                                    <Text as="span" 
                                        color={
                                            auction.getDayStats(day).totalContribution > 0 
                                                ? "green.400" 
                                                : auction.getDayStats(day).projectedContribution > 0 
                                                    ? "purple.400" 
                                                    : "gray.400"
                                        } 
                                        ml={1}
                                    >
                                        {auction.getDayStats(day).totalUserContribution > 0 
                                            ? auction.getDayStats(day).totalUserContribution.toFixed(2) 
                                            : "0"} SOL
                                    </Text>
                                </Text>
                                <Text fontSize="sm" color="gray.300">
                                    Share: {auction.getDayStats(day).sharePercentage.toFixed(2)}% | 
                                    <Text as="span" 
                                        color={
                                            auction.getDayStats(day).totalContribution > 0 
                                                ? "green.400" 
                                                : auction.getDayStats(day).projectedContribution > 0 
                                                    ? "purple.400" 
                                                    : "gray.300"
                                        } 
                                        ml={1}
                                    >
                                        {formatNumber(auction.getDayStats(day).sharePercentage * calculateDailyValues(day).dayAllocation / 100)} JINU
                                    </Text>
                                </Text>
                            </VStack>
                        </Box>

                        {day === 51 ? (
                            <>
                                <Box mb={2}>
                                    <HStack spacing={2} align="center">
                                        <SolanaLogo boxSize={5} color="#FF9100" />
                                        <Text color="whiteAlpha.600" fontSize="sm">Total SOL</Text>
                                    </HStack>
                                    <Text color="white" fontSize="xl" fontWeight="bold" lineHeight="1.2">
                                        {auction.auctionState.totalSolRaised?.toFixed(2) || "0.00"}
                                    </Text>
                                    <Text fontSize="xs" color="whiteAlpha.600" mt="1">
                                        ${(auction.auctionState.totalSolRaised * auction.SOL_PRICE_USD).toLocaleString()}
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.700" mt={2}>
                                        Total Participants
                                    </Text>
                                    <Text fontSize="lg" color="yellow.400" fontWeight="bold">
                                        {(() => {
                                            // Calculate participants count - always add 1 for the user if there are any contributions
                                            const hasUserContributions = Object.values(auction.auctionState.userContributions || {}).some(dayContribs => dayContribs.length > 0);
                                            const totalParticipants = hasUserContributions ? 
                                                (auction.auctionState.totalParticipants || 4273) + 1 : (auction.auctionState.totalParticipants || 4273);
                                            console.log("DailyBoxes participants:", {
                                                hasUserContributions,
                                                totalParticipants,
                                                userContributionsCheck: Object.values(auction.auctionState.userContributions || {}).some(dayContribs => dayContribs.length > 0)
                                            });
                                            return totalParticipants.toLocaleString();
                                        })() || "0"}
                                    </Text>
                                </Box>
                                <Button
                                    variant="outline"
                                    colorScheme="yellow"
                                    size="sm"
                                    width="100%"
                                    onClick={() => alert("Claim Day is Coming!")}
                                    bg="whiteAlpha.50"
                                    borderColor="yellow.400"
                                    color="yellow.400"
                                    _hover={{
                                        bg: "yellow.400",
                                        color: "black",
                                        transform: "scale(1.02)"
                                    }}
                                    _active={{
                                        bg: "yellow.500",
                                        transform: "scale(0.98)"
                                    }}
                                    transition="all 0.2s"
                                >
                                    Claim
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    colorScheme={getDayStatus(day) === 'today' ? "green" : "yellow"}
                                    size="sm"
                                    width="100%"
                                    borderRadius="full"
                                    onClick={() => handleDaySelection(day)}
                                    _hover={{
                                        bg: getDayStatus(day) === 'today' ? "green.500" : "yellow.500",
                                        color: "white"
                                    }}
                                    transition="all 0.2s"
                                    isDisabled={getDayStatus(day) === 'past'}
                                    opacity={getDayStatus(day) === 'past' ? 0.5 : 1}
                                >
                                    Reserve
                                </Button>

                                <Button
                                    leftIcon={<FaTwitter />}
                                    size="xs"
                                    colorScheme="twitter"
                                    variant="ghost"
                                    onClick={() => handleTwitterShare(day)}
                                    fontSize="xs"
                                    px={2}
                                    height="24px"
                                    width="115%"
                                    borderRadius="full"
                                    _hover={{ 
                                        color: '#1DA1F2'
                                    }}
                                >
                                    Share & Get 10% More!
                                </Button>
                            </>
                        )}
                    </VStack>
                </VStack>
            </MotionBox>
        );
    };

    return (
        <Box position="relative">
            <Box 
                overflowX="auto" 
                css={{
                    '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '4px',
                        '&:hover': {
                            background: 'rgba(255, 255, 255, 0.3)',
                        },
                    },
                }}
            >
                <SimpleGrid
                    columns={compact ? 1 : { base: 1, md: 2, lg: 4 }} 
                    spacing={4} 
                    py={4}
                    px={2}
                    minW={compact ? "auto" : "1000px"}
                >
                    {Array.from({ length: TOTAL_DAYS }, (_, i) => {
                        const day = i + 1;
                        // Skip rendering days that are in the excludeDays array
                        if (excludeDays.includes(day)) {
                            return null;
                        }
                        return renderDayBox(day);
                    })}
                    
                    {/* Original Day 51 DEX Listing Box */}
                    {(() => {
                        const day = DEX_LISTING_DAY;
                        console.log('DexListingBox calculation starting...');
                        let dailyBreakdown = [];
                        const totalJinu = Array.from({ length: AUCTION_DAYS }, (_, i) => {
                            const day = i + 1;
                            const dayStats = auction.getDayStats(day);
                            const dayJinu = dayStats.projectedTokens || 0;
                            dailyBreakdown.push({
                                day,
                                userContribution: dayStats.totalContribution,
                                totalContributions: dayStats.totalContributions,
                                sharePercentage: dayStats.sharePercentage,
                                dayAllocation: dayStats.dayAllocation,
                                projectedTokens: dayJinu
                            });
                            return dayJinu;
                        }).reduce((sum, tokens) => sum + tokens, 0);

                        console.log('DexListingBox daily breakdown:', dailyBreakdown);
                        console.log('DexListingBox total JINU:', totalJinu);

                        return (
                            <DexListingBox
                                key={day}
                                totalDexUsers={auction.auctionState.totalParticipants || 0}
                                totalDexSol={auction.auctionState.totalSolRaised || 0}
                                totalUserJinu={totalJinu}
                                handleTwitterShare={() => handleTwitterShare(day)}
                                handleDayClick={() => handleDaySelection(day)}
                            />
                        );
                    })()}
                </SimpleGrid>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay backdropFilter="blur(4px)" />
                <ModalContent bg="gray.800" borderColor="whiteAlpha.200" borderWidth="1px" position="relative">
                    {/* Custom close button in the top-right corner */}
                    <Box 
                        position="absolute" 
                        top="10px" 
                        right="10px" 
                        zIndex="10"
                        cursor="pointer"
                        onClick={onClose}
                        bg="gray.700"
                        borderRadius="md"
                        p="2"
                        _hover={{ bg: "gray.600" }}
                    >
                        <Text fontSize="lg" fontWeight="bold" color="white">×</Text>
                    </Box>
                    
                    <ModalHeader color="white">
                        Contribute to Day {selectedDayState !== null ? selectedDayState : ''}
                    </ModalHeader>
                    
                    <ModalBody>
                        <VStack spacing={4}>
                            {!auction.wallet.connected ? (
                                <Button
                                    w="100%"
                                    size="lg"
                                    variant="outline"
                                    borderColor="#00E676"
                                    color="#00E676"
                                    _hover={{
                                        bg: "#00E676",
                                        color: "black"
                                    }}
                                    onClick={handleConnectWallet}
                                    borderRadius="full"
                                    borderWidth="2px"
                                    transition="all 0.2s"
                                >
                                    Connect Wallet
                                </Button>
                            ) : (
                                <VStack spacing={4} w="full">
                                    <Text color="white">
                                        Enter amount to contribute (SOL):
                                    </Text>
                                    <Input
                                        type="number"
                                        value={contributionAmount}
                                        onChange={(e) => setContributionAmount(e.target.value)}
                                        placeholder="0.0"
                                        color="white"
                                        borderColor="whiteAlpha.300"
                                        _hover={{ borderColor: "whiteAlpha.400" }}
                                        _focus={{ borderColor: "#00E676" }}
                                    />
                                    <HStack w="full" spacing={2} flexWrap="wrap" justify="center">
                                        {[0.1, 0.2, 0.5, 1, 5, 10, 20].map((amount) => {
                                            let buttonColor;
                                            if (amount === 1) buttonColor = "pink.400";
                                            else if (amount === 5) buttonColor = "blue.400";
                                            else if (amount === 10) buttonColor = "yellow.400";
                                            else if (amount === 20) buttonColor = "purple.400";
                                            else buttonColor = "#00E676";

                                            return (
                                                <Button
                                                    key={amount}
                                                    size="sm"
                                                    variant="outline"
                                                    borderColor={buttonColor}
                                                    color={buttonColor}
                                                    _hover={{
                                                        bg: buttonColor,
                                                        color: "black"
                                                    }}
                                                    onClick={() => setContributionAmount(amount.toString())}
                                                    borderRadius="full"
                                                    borderWidth="2px"
                                                    transition="all 0.2s"
                                                >
                                                    {amount} SOL
                                                </Button>
                                            );
                                        })}
                                    </HStack>
                                    <Button
                                        w="100%"
                                        size="lg"
                                        bg="#00E676"
                                        color="black"
                                        _hover={{
                                            bg: "#00ff85"
                                        }}
                                        onClick={handleModalContribute}
                                        borderRadius="full"
                                        isDisabled={!contributionAmount || parseFloat(contributionAmount) <= 0}
                                    >
                                        Contribute
                                    </Button>
                                </VStack>
                            )}
                        </VStack>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default DailyBoxes;
